import { api } from "./client";
import type { ApiResponse } from "./types";

// ============================================================================
// Member conversations — what two people say after they wave at each other.
//
// A conversation exists only between people with a MUTUAL wave; the server
// refuses everyone else. One conversation per pair, so opening it twice
// resumes it. Either side can end it, and the transcript stays readable.
//
// Until push arrives, an app polls ONE cheap call: the unread count. It also
// carries `lastMessageAt`, the newest message in any of the member's
// conversations, so the app fetches the list or the open conversation only
// when that time moves.
// ============================================================================

export interface ConversationPeer {
  id: string;
  name: string;
  avatarUrl: string | null;
  city: string | null;
}

/** The thread's message shape, also used for the inbox line. */
export interface ConversationMessage {
  id: string;
  /** Null exactly when the message was removed. */
  body: string | null;
  isRedacted: boolean;
  isMine: boolean;
  createdAt: string;
}

export interface Conversation {
  id: string;
  /** Known values: open, closed. */
  status: string;
  peer: ConversationPeer;
  /** The newest message. Null until somebody writes. */
  lastMessage: ConversationMessage | null;
  lastMessageAt: string | null;
  unreadCount: number;
  createdAt: string;
}

export interface ConversationsPage {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export type ConversationReportReason =
  | "harassment"
  | "hate_speech"
  | "spam"
  | "misinformation"
  | "self_harm_concern"
  | "impersonation"
  | "other";

/** Newest first, each with its last message and the caller's unread count. */
export function listConversations(params?: { page?: number; limit?: number }) {
  return api.get<ApiResponse<{ conversations: Conversation[]; meta: ConversationsPage }>>(
    "/conversations",
    params,
  );
}

/** The call to poll: the badge, and the newest message time to compare. */
export function getConversationsUnreadCount() {
  return api.get<ApiResponse<{ unreadCount: number; lastMessageAt: string | null }>>(
    "/conversations/unread-count",
  );
}

/** One conversation with its messages, newest first. Reading it marks your side read. */
export function getConversation(conversationId: string, params?: { page?: number; limit?: number }) {
  return api.get<
    ApiResponse<{ conversation: Conversation; messages: ConversationMessage[]; meta: ConversationsPage }>
  >(`/conversations/${conversationId}`, params);
}

/** Open, or resume, the conversation with a mutual. */
export function openConversation(peerUserId: string) {
  return api.post<ApiResponse<{ conversation: Conversation; resumed: boolean }>>("/conversations", {
    peerUserId,
  });
}

/** One `idempotencyKey` per send; resending with the same key must return the same message. */
export function sendConversationMessage(conversationId: string, body: string, idempotencyKey?: string) {
  return api.post<ApiResponse<ConversationMessage>>(`/conversations/${conversationId}/messages`, {
    body,
    ...(idempotencyKey ? { idempotencyKey } : {}),
  });
}

export function markConversationRead(conversationId: string) {
  return api.post<void>(`/conversations/${conversationId}/read`, {});
}

/** End it: new messages are refused, the transcript stays readable. */
export function closeConversation(conversationId: string) {
  return api.post<void>(`/conversations/${conversationId}/close`, {});
}

export function reportConversationMessage(
  messageId: string,
  reason: ConversationReportReason,
  details?: string,
) {
  return api.post<ApiResponse<{ id: string; status: string; createdAt: string }>>(
    `/conversations/messages/${messageId}/report`,
    { reason, ...(details ? { details } : {}) },
  );
}
